<template>
  <div v-if="enabled" class="glitch-overlay-container">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  enabled: { type: Boolean, default: true },
  intensity: { type: Number, default: 1.0 },
  frequency: { type: Number, default: 1.0 },
  blendMode: { type: String, default: 'screen' }
})

const canvasRef = ref(null)

let gl, baseProgram, glitchProgram, baseTexture, baseFramebuffer
let vaoBase, vaoGlitch, positionBuffer
let startTime = 0
let animationId = null
let locBase = null
let locGlitch = null

const vs = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`

const fsBase = `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
out vec4 fragColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec2 uv = gl_FragCoord.xy / iResolution.xy;
  float noise = hash(uv * 100.0 + iTime * 0.1);
  vec3 col = vec3(noise) * 0.5 + 0.5;
  fragColor = vec4(col, 1.0);
}
`

const fsGlitch = `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform sampler2D iChannel0;
out vec4 fragColor;

uint rand(uint x) {
  const uint k = 3282017729u;
  x *= k; x ^= k;
  return x;
}

uint rand(uvec2 x) {
  return rand(x.x) * (rand(x.y) ^ 0xAAAAAAAAu);
}

float u2f(uint x) {
  x >>= 9u;
  x |= 0x3f800000u;
  return uintBitsToFloat(x) - 1.0;
}

float timedRand(float t, float d) {
  float r = u2f(rand(uint(t)));
  if (d < 0.5)
    r = r * d * 2.0;
  else
    r = mix(r, 1.0, d * 2.0 - 1.0);
  return float(fract(t) > r);
}

float blockRand(vec2 uv, int octaves, float time) {
  float r = 0.0;
  float d = 0.0;
  float s = 1.0;
  for (int i = 0; i < octaves; ++i) {
    r += u2f(rand(uvec2(uv / s) * uint(floor(time)))) * s;
    d += s;
    s *= 0.5;
  }
  return r / d;
}

void main() {
  vec2 fragCoord = gl_FragCoord.xy;
  vec2 uv = fragCoord / iResolution.xy;
  vec2 cuv = uv;
  cuv.x *= iResolution.x / iResolution.y;

  float safeTime = max(iTime, 1.0);
  float t = safeTime + sin(safeTime);

  float mt = t * 0.345;
  float m = blockRand(cuv, 4, mt) * pow(1.0 - fract(mt), 8.0);

  vec4 tex = textureLod(iChannel0, uv - vec2(floor(m * 4.0) / 4.0 * 0.025, 0.0), m * 6.0);

  float gr = blockRand(cuv / vec2(16.0, 1.0) + safeTime * 8.0, 8, safeTime);
  vec4 glitch1 = vec4(fract(sin(gr * 123.456)), fract(sin(gr * 234.567)), fract(sin(gr * 345.678)), 1.0);

  gr = mix(gr, blockRand(cuv * vec2(1024.0, 1.0) + safeTime * 8.0, 8, safeTime), 0.01);
  vec4 glitch2 = vec4(fract(sin(gr * 123.456)), fract(sin(gr * 234.567)), fract(sin(gr * 345.678)), 1.0);

  vec4 glitch = sqrt(glitch1 * glitch2);

  float br = blockRand(cuv / vec2(16.0, 1.0) * 8.0, 8, t);
  float mask = float(br > 0.8 + floor(sin(t) * 2.0) * 0.05);

  float li = fragCoord.y * iResolution.x + fragCoord.x;
  float lt = fract(li / (iResolution.x * iResolution.y) + iTime);
  vec4 l = vec4(lt, fract(lt * 256.0), fract(lt * 65536.0), 1.0);

  float glitchTiming = timedRand(t * 1.414, 0.5);
  vec4 result = mix(tex, mix(mix(tex, tex * l + l, 0.1), glitch, mask), glitchTiming);

  float glitchIntensity = mask * glitchTiming;
  float alpha = min(glitchIntensity * 3.0, 1.0);
  fragColor = vec4(result.rgb, alpha);
}
`

function compile(src, type) {
  const sh = gl.createShader(type)
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw gl.getShaderInfoLog(sh)
  return sh
}

function link(vs, fs) {
  const p = gl.createProgram()
  gl.attachShader(p, vs)
  gl.attachShader(p, fs)
  gl.linkProgram(p)
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw gl.getProgramInfoLog(p)
  return p
}

function createFramebuffer(w, h) {
  if (baseFramebuffer) gl.deleteFramebuffer(baseFramebuffer)
  if (baseTexture) gl.deleteTexture(baseTexture)

  baseTexture = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, baseTexture)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)

  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

  baseFramebuffer = gl.createFramebuffer()
  gl.bindFramebuffer(gl.FRAMEBUFFER, baseFramebuffer)
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, baseTexture, 0)

  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  gl.bindTexture(gl.TEXTURE_2D, null)
}

function resize() {
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const w = Math.floor(window.innerWidth * dpr)
  const h = Math.floor(window.innerHeight * dpr)

  const c = canvasRef.value
  if (!c) return

  if (c.width !== w || c.height !== h) {
    c.width = w
    c.height = h
    createFramebuffer(w, h)
  }
}

function setup() {
  positionBuffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW)

  vaoBase = gl.createVertexArray()
  gl.bindVertexArray(vaoBase)
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  let loc = gl.getAttribLocation(baseProgram, 'position')
  gl.enableVertexAttribArray(loc)
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

  vaoGlitch = gl.createVertexArray()
  gl.bindVertexArray(vaoGlitch)
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  loc = gl.getAttribLocation(glitchProgram, 'position')
  gl.enableVertexAttribArray(loc)
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

  gl.bindVertexArray(null)

  locBase = {
    res: gl.getUniformLocation(baseProgram, 'iResolution'),
    time: gl.getUniformLocation(baseProgram, 'iTime')
  }

  locGlitch = {
    res: gl.getUniformLocation(glitchProgram, 'iResolution'),
    time: gl.getUniformLocation(glitchProgram, 'iTime'),
    tex: gl.getUniformLocation(glitchProgram, 'iChannel0')
  }

  gl.useProgram(glitchProgram)
  gl.uniform1i(locGlitch.tex, 0)
}

function render() {
  const c = canvasRef.value
  if (!c) return

  const t = ((performance.now() - startTime) / 1000) * props.frequency

  gl.bindFramebuffer(gl.FRAMEBUFFER, baseFramebuffer)
  gl.viewport(0, 0, c.width, c.height)
  gl.clearColor(0, 0, 0, 0)
  gl.clear(gl.COLOR_BUFFER_BIT)

  gl.useProgram(baseProgram)
  gl.bindVertexArray(vaoBase)
  gl.uniform2f(locBase.res, c.width, c.height)
  gl.uniform1f(locBase.time, t)
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  gl.viewport(0, 0, c.width, c.height)
  gl.clear(gl.COLOR_BUFFER_BIT)

  gl.useProgram(glitchProgram)
  gl.bindVertexArray(vaoGlitch)
  gl.uniform2f(locGlitch.res, c.width, c.height)
  gl.uniform1f(locGlitch.time, t)
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, baseTexture)
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

  animationId = requestAnimationFrame(render)
}

watch(() => props.blendMode, m => {
  if (canvasRef.value) canvasRef.value.style.mixBlendMode = m
})

onMounted(() => {
  const c = canvasRef.value
  gl = c.getContext('webgl2', { alpha: true, premultipliedAlpha: false, antialias: false })

  gl.enable(gl.BLEND)
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

  c.style.mixBlendMode = props.blendMode

  const vsObj = compile(vs, gl.VERTEX_SHADER)
  const fsBaseObj = compile(fsBase, gl.FRAGMENT_SHADER)
  const fsGlitchObj = compile(fsGlitch, gl.FRAGMENT_SHADER)

  baseProgram = link(vsObj, fsBaseObj)
  glitchProgram = link(vsObj, fsGlitchObj)

  resize()
  setup()
  startTime = performance.now()

  window.addEventListener('resize', resize)
  render()
})

onUnmounted(() => {
  if (animationId) cancelAnimationFrame(animationId)
  window.removeEventListener('resize', resize)
})
</script>

<style scoped>
.glitch-overlay-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
}
canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: transparent;
}
</style>
